import { Platform, Switch as RNSwitch, Pressable, View } from 'react-native';
import { useColors } from '../lib/colors';

interface BrandSwitchProps {
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
  accessibilityLabel?: string;
  testID?: string;
}

/**
 * Forest-tinted toggle switch.
 *
 * On native (iOS/Android) we use React Native's Switch with explicit
 * trackColor + thumbColor so Android's Material 3 default tint doesn't
 * give us a forest-on-forest thumb. On web, react-native-web's Switch
 * ignores thumbColor and renders a browser-tinted thumb that clashes
 * with the forest track, so we render a small Pressable + circular thumb
 * View directly and animate the position via transform.
 */
export function BrandSwitch({
  value,
  onValueChange,
  disabled,
  accessibilityLabel,
  testID,
}: BrandSwitchProps) {
  const c = useColors();
  const activeTrackColor = '#2B7A50';

  if (Platform.OS === 'web') {
    return (
      <Pressable
        testID={testID}
        accessibilityRole="switch"
        accessibilityState={{ checked: value, disabled }}
        accessibilityLabel={accessibilityLabel}
        onPress={() => onValueChange(!value)}
        disabled={disabled}
        style={{
          width: 44,
          height: 24,
          borderRadius: 12,
          backgroundColor: value ? activeTrackColor : c.border,
          padding: 2,
          opacity: disabled ? 0.5 : 1,
          justifyContent: 'center',
          // @ts-expect-error – web-only style, RN types don't know about transition
          transition: 'background-color 0.15s ease',
        }}
      >
        <View
          style={{
            width: 20,
            height: 20,
            borderRadius: 10,
            backgroundColor: '#ffffff',
            transform: [{ translateX: value ? 20 : 0 }],
            shadowColor: '#000',
            shadowOpacity: 0.2,
            shadowRadius: 2,
            shadowOffset: { width: 0, height: 1 },
            // @ts-expect-error – web-only style, RN types don't know about transition
            transition: 'transform 0.15s ease',
          }}
        />
      </Pressable>
    );
  }

  return (
    <RNSwitch
      testID={testID}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      trackColor={{ false: c.border, true: activeTrackColor }}
      thumbColor="#ffffff"
      ios_backgroundColor={c.border}
    />
  );
}
